import { memo, useMemo } from 'react'
import type { QuestionResponse, QuestionSummary } from '../types'
import { toQuestionSnippet } from '../utils/renderContent'

const DIFFICULTY_STYLES: Record<string, string> = {
  EASY: 'bg-green-100 text-green-700',
  MEDIUM: 'bg-yellow-100 text-yellow-700',
  HARD: 'bg-red-100 text-red-700',
}

const toTitleCase = (value?: string | null) => (value ? value.charAt(0) + value.slice(1).toLowerCase() : '')

interface QuestionListItemProps {
  /** Full question, or a batch summary (text only, no options) */
  question: QuestionResponse | QuestionSummary
  questionNumber: number
  selected: boolean
  onSelect: (index: number) => void
}

function QuestionListItem({ question, questionNumber, selected, onSelect }: QuestionListItemProps) {
  const text = 'questionData' in question ? question.questionData.text : question.questionText
  const options = 'questionData' in question ? question.questionData.options : []
  const snippet = useMemo(() => toQuestionSnippet(text), [text])
  const hasImage = /#img\[/.test(text) || options.some((o) => /#img\[/.test(o))

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(questionNumber - 1)}
        aria-current={selected ? 'true' : undefined}
        data-question-index={questionNumber - 1}
        className={`w-full border-b border-l-4 border-b-gray-100 px-4 py-3 text-left transition-colors ${
          selected ? 'border-l-blue-600 bg-blue-50' : 'border-l-transparent hover:bg-gray-50'
        }`}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className={`text-sm font-semibold ${selected ? 'text-blue-700' : 'text-gray-900'}`}>
            Q{questionNumber}
          </span>
          <div className="flex items-center gap-1.5">
            {hasImage && <span className="text-xs text-gray-400">Diagram</span>}
            {question.difficultyLevel && (
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${DIFFICULTY_STYLES[question.difficultyLevel] ?? 'bg-gray-100 text-gray-600'}`}
              >
                {toTitleCase(question.difficultyLevel)}
              </span>
            )}
          </div>
        </div>
        <div
          className="line-clamp-2 text-sm leading-snug text-gray-600"
          dangerouslySetInnerHTML={{ __html: snippet }}
        />
        <p className="mt-1 text-xs text-gray-400">{toTitleCase(question.questionType)}</p>
      </button>
    </li>
  )
}

export default memo(QuestionListItem)
